import { useMemo, useState } from "react";
import { SlidersHorizontal, X, Star, Bookmark } from "lucide-react";
import { toast } from "sonner";

export const EMP_FIELDS = [
  { k: "nationality", label: "الجنسية", get: (e) => e.nationality },
  { k: "profession", label: "المهنة", get: (e) => e.profession },
  { k: "app_vehicle_type", label: "مركبة التطبيق", get: (e) => e.app_vehicle_type },
  { k: "vehicle_type", label: "نوع المركبة", get: (e) => e.vehicle_type },
  { k: "platform", label: "المنصة", get: (e) => e.platforms || [] },
  { k: "contract_type", label: "نوع العقد", get: (e) => e.contract_type },
  { k: "employee_status", label: "حالة الموظف", get: (e) => e.employee_status || e.tashark_status },
  { k: "contract_status", label: "حالة العقد", get: (e) => ({ valid: "ساري", expiring_soon: "يقرب", expired: "منتهٍ" }[e.contract_status] || "بدون عقد") },
];
const vals = (f, e) => {
  const v = [].concat(f.get(e) ?? []).map((x) => String(x).trim()).filter(Boolean);
  return v.length ? v : ["غير محدد"];
};

export const matchFields = (row, fields, xf) => fields.every((f) => !xf[f.k] || vals(f, row).includes(xf[f.k]));
export const matchExtra = (e, xf) => matchFields(e, EMP_FIELDS, xf);

const loadP = (k) => { try { return JSON.parse(localStorage.getItem(`wz_presets_${k}`) || "[]"); } catch { return []; } };

const Presets = ({ presetKey, snapshot, onApply }) => {
  const [list, setList] = useState(() => loadP(presetKey));
  const persist = (l) => { setList(l); localStorage.setItem(`wz_presets_${presetKey}`, JSON.stringify(l)); };
  const save = () => {
    const name = (window.prompt("اسم الفلتر المفضل (مثال: سعوديون عقودهم تقرب)") || "").trim();
    if (!name) return;
    persist([...list.filter((p) => p.name !== name), { name, state: snapshot }]);
    toast.success(`حُفظ الفلتر «${name}»`);
  };
  return (
    <>
      <button type="button" onClick={save} data-testid={`${presetKey}-preset-save`}
        className="inline-flex items-center gap-1 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-100">
        <Star className="h-3.5 w-3.5" /> حفظ كمفضل
      </button>
      {list.map((p, i) => (
        <span key={p.name} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs text-amber-800" data-testid={`${presetKey}-preset-${i}`}>
          <button type="button" onClick={() => onApply(p.state)} className="inline-flex items-center gap-1 font-semibold hover:underline" data-testid={`${presetKey}-preset-apply-${i}`}>
            <Bookmark className="h-3 w-3" /> {p.name}
          </button>
          <button type="button" onClick={() => persist(list.filter((x) => x.name !== p.name))} title="حذف" data-testid={`${presetKey}-preset-del-${i}`}><X className="h-3 w-3" /></button>
        </span>
      ))}
    </>
  );
};

export const ExtraFilters = ({ rows, fields, xf, setXf, presetKey, snapshot, onApply, onClearAll }) => {
  const [open, setOpen] = useState(false);
  const opts = useMemo(() => Object.fromEntries(fields.map((f) => {
    const others = Object.fromEntries(Object.entries(xf).filter(([k]) => k !== f.k));
    const c = {};
    rows.filter((e) => matchFields(e, fields, others)).forEach((e) => vals(f, e).forEach((v) => { c[v] = (c[v] || 0) + 1; }));
    if (xf[f.k] && !c[xf[f.k]]) c[xf[f.k]] = 0;
    return [f.k, Object.entries(c).sort((a, b) => (a[0] === "غير محدد") - (b[0] === "غير محدد") || b[1] - a[1])];
  })), [rows, fields, xf]);
  const useful = fields.filter((f) => xf[f.k] || opts[f.k].some(([v]) => v !== "غير محدد"));
  const n = fields.filter((f) => xf[f.k]).length;
  return (
    <div className="space-y-2" data-testid={`${presetKey}-extra-filters`}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${presetKey}-extra-filters-toggle`}
          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${open || n ? "border-blue-300 bg-blue-50 text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
          <SlidersHorizontal className="h-4 w-4" /> فلاتر {n ? `(${n})` : ""}
        </button>
        {fields.filter((f) => xf[f.k]).map((f) => (
          <span key={f.k} className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs text-blue-700" data-testid={`${presetKey}-xf-chip-${f.k}`}>
            {f.label}: <b>{xf[f.k]}</b>
            <button type="button" onClick={() => setXf({ ...xf, [f.k]: "" })}><X className="h-3 w-3" /></button>
          </span>
        ))}
        {n > 0 && <button type="button" onClick={() => (onClearAll ? onClearAll() : setXf({}))} className="text-xs font-semibold text-rose-600 hover:underline" data-testid={`${presetKey}-xf-clear`}>مسح الفلاتر</button>}
        {onApply && <Presets presetKey={presetKey} snapshot={snapshot ?? { xf }} onApply={onApply} />}
      </div>
      {open && (
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-slate-200 bg-slate-50/60 p-3 sm:grid-cols-4">
          {!useful.length && <p className="col-span-full py-2 text-center text-xs text-slate-400" data-testid={`${presetKey}-xf-empty`}>لا توجد بيانات كافية للتصفية في هذا التقرير</p>}
          {useful.map((f) => (
            <label key={f.k} className="space-y-1 text-xs text-slate-500">
              <span>{f.label}</span>
              <select value={xf[f.k] || ""} onChange={(e) => setXf({ ...xf, [f.k]: e.target.value })} data-testid={`${presetKey}-xf-${f.k}`}
                className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
                <option value="">الكل</option>
                {opts[f.k].map(([v, c]) => <option key={v} value={v}>{`${v} (${c})`}</option>)}
              </select>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export const EmpExtraFilters = (p) => <ExtraFilters rows={p.employees} fields={EMP_FIELDS} presetKey="emp" {...p} />;
